import {CommonService} from '../../../services/common.service';
import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {ExportCsvService} from '../../../services/export-csv.service';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {PdfCreatorComponent} from '../../../common/pdf-creator/pdf-creator.component';
import {ToastMessagesService} from '../../../services/toast-messages.service';
import {UsedBudgetService} from '../../../services/used-budget.service';
import {LoadingService} from '../../../services/loading.service';

interface IMaterial {
  id: number;
  name: string;
}

interface IRegional {
  id: number;
  name: string;
}

@Component({
  selector: 'app-budget-table',
  styleUrls: ['./budget-table.component.scss'],
  templateUrl: './budget-table.component.html'
})
export class BudgetTableComponent implements OnInit {
  @Input() columns: any;
  @Input() hasFooter: boolean;
  @ViewChild('paginator', {static: false}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: false}) sort: MatSort;

  private initialData: any;
  public categories: any[];
  public dataSource: MatTableDataSource<any>;
  public inspectors: any[];
  public isLed: any[];
  public materials: IMaterial[];
  public pendingRows: any[];
  public regionals: IRegional[];
  public salesGroups: any[];
  public selectedCategories: any[];
  public selectedInspectors: any[];
  public selectedMaterials: IMaterial[];
  public selectedRegionals: IRegional[];
  public selectedSalesGroup: any[];
  public tableHeight: number;

  constructor(private commonService: CommonService,
              private exportCsvService: ExportCsvService,
              private matDialog: MatDialog,
              private toastMessagesService: ToastMessagesService,
              private usedBudgetService: UsedBudgetService,
              private loadingService: LoadingService) {
    this.categories = [];
    this.dataSource = new MatTableDataSource<any>([]);
    this.inspectors = [];
    this.isLed = [];
    this.materials = [];
    this.pendingRows = [];
    this.regionals = [];
    this.salesGroups = [];
    this.selectedCategories = [];
    this.selectedInspectors = [];
    this.selectedMaterials = [];
    this.selectedRegionals = [];
    this.selectedSalesGroup = [];
    this.tableHeight = window.innerHeight - 380;
  }

  public async ngOnInit(): Promise<void> {
    try {
      this.loadingService.show();
      await this.initDataSource();
      this.loadingService.hide();
      this.materials = await this.usedBudgetService.fetchMaterials();
      this.salesGroups = await this.usedBudgetService.fetchSalesGroups();
      this.regionals = await this.usedBudgetService.fetchRegionals();
      this.inspectors = await this.usedBudgetService.fetchInspectors();
      this.categories = await this.usedBudgetService.fetchCategories();
    } catch (e) {
      this.loadingService.hide();
      this.toastMessagesService.toastErrorMessage(e.error.message);
    }
  }

  async initDataSource(force?: boolean) {
    this.initialData = await this.usedBudgetService.fetchBudget(force);
    const temp = JSON.parse(JSON.stringify(this.initialData));
    this.dataSource = new MatTableDataSource<any>([...temp]);
    this.commonService.setPaginatorTexts(this.paginator);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  reCalculate(row): void {
    try {
      // await this.update(row);
      this.clearRow(row, true);
      const index = this.dataSource.data.findIndex(t => t.id === row.id);
      const previous = +this.dataSource.data[index].redistributions;
      const value = +row['new-redistribution'];
      const newValue = previous + value;

      const prevTotal = +this.dataSource.data[index].available_budget;
      const prevPhysical = +this.dataSource.data[index].physical_budget;
      const newTotal = prevTotal + value;
      const newPhysical = prevPhysical + value;

      if (newTotal < 0) {
        this.toastMessagesService.toastMessages(`Δεν επαρκεί η ποσότητα των ψυγείων για την ανακατανομή`);
        this.dataSource.data[index]['new-redistribution'] = '';
        return;
      }

      this.dataSource.data[index].available_budget = newTotal;
      this.dataSource.data[index].physical_budget = newPhysical;
      this.dataSource.data[index].redistributions = newValue;

      this.addToQueue(row);
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.error.message);
    }
  }

  getTotal(field: string) {
    return this.dataSource ? this.dataSource.data.map(t => t[field]).reduce((acc, value) => acc + +value, 0) : '-';
  }

  calculateRowSum(row: any) {
    return row.cost + row.number1 + row.number2 + row.number3 + row.number4;
  }

  displayCategory(category: any) {
    return category.id === 0 ? category.name : `${category.category} (${category.name})`;
  }

  isAllSelected(field) {
    switch (field) {
      case 'regionals':
        return this.selectedRegionals.some(r => r.id === 0);
      case 'materials':
        return this.selectedMaterials.some(r => r.id === 0);

      case 'inspectors':
        return this.selectedInspectors.some(r => r.id === 0);

      case 'categories':
        return this.selectedCategories.some(r => r.id === 0);

      case 'salesGroup':
        return this.selectedSalesGroup.some(r => r.id === 0);

    }
  }

  filterOut() {
    const data = [...JSON.parse(JSON.stringify(this.initialData))];
    if (this.initialState()) {
      this.dataSource.data = data;
      return;
    }

    const selectedFilters = this.calculateSelectedFilters();

    const result = data.filter(item => selectedFilters.every(f => f(item)));
    this.dataSource.data = [...result];
  }

  calculateSelectedFilters() {
    const regionalIds = this.selectedRegionals.length > 0 ? this.selectedRegionals.map(r => r.id) : [];
    const materialIds = this.selectedMaterials.length > 0 ? this.selectedMaterials.map(r => r.id) : [];
    const saleGroupIds = this.selectedSalesGroup.length > 0 ? this.selectedSalesGroup.map(r => r.id) : [];
    const inspectorIds = this.selectedInspectors.length > 0 ? this.selectedInspectors.map(r => r.id) : [];
    const categoryIds = this.selectedCategories.length > 0 ? this.selectedCategories.map(r => r.id) : [];
    const isLedIds = this.isLed.length > 0 ? this.isLed.map(r => r.id) : [];
    const selectedFilters = [];
    const filters = {
      selectRegions: item => regionalIds.some(id => id === item.regional_id),
      selectMaterials: item => materialIds.some(id => id === item.material_id),
      selectSalesGroup: item => saleGroupIds.some(id => id === item.sales_group_id),
      selectInspectors: item => inspectorIds.some(id => id === item.inspector_id),
      selectCategories: item => categoryIds.some(id => id === item.category_id),
      selectLed: item => isLedIds.some(id => id === item.is_led)
    };

    if (regionalIds.length > 0) {
      selectedFilters.push(filters.selectRegions);
    }
    if (materialIds.length > 0) {
      selectedFilters.push(filters.selectMaterials);
    }

    if (saleGroupIds.length > 0) {
      selectedFilters.push(filters.selectSalesGroup);
    }

    if (inspectorIds.length > 0) {
      selectedFilters.push(filters.selectInspectors);
    }
    if (categoryIds.length > 0) {
      selectedFilters.push(filters.selectCategories);
    }
    if (isLedIds.length > 0) {
      selectedFilters.push(filters.selectLed);
    }

    return selectedFilters;
  }

  initialState() {
    return this.selectedCategories.length === 0 &&
      this.selectedInspectors.length === 0 &&
      this.selectedSalesGroup.length === 0 &&
      this.selectedMaterials.length === 0 && this.selectedRegionals.length === 0 && this.isLed.length === 0;
  }

  addToQueue(row) {
    if (row['new-redistribution']) {
      const initialIndex = this.initialData.findIndex(t => t.id === row.id);

      const initialRow = this.initialData[initialIndex];
      // tslint:disable-next-line:variable-name
      const old_redistributions = +initialRow.redistributions;
      const sentObject = {
        rowId: row.id,
        redistributions: row['new-redistribution'],
        old_redistributions,
        material_id: row.material_id,
        sales_group_id: row.sales_group_id,
        is_led: row.is_led,
        is_led_name: row.is_led_name,
        category_id: row.category_id,
        category_name: row.category_name
      };

      this.pendingRows.push(sentObject);
    }
  }

  async update() {
    if (this.getTotal('new-redistribution') && this.getTotal('redistributions') !== 0) {
      this.toastMessagesService.toastMessages(`Το άθροισμα των ανακατονομών πρέπει να ειναι ίσο με 0`);
      return;
    }
    try {
      await this.usedBudgetService.updateRedistribution(this.pendingRows);
      this.toastMessagesService.toastMessages(`Η ενημέρωση ολοκληρώθηκε με επιτυχία`);
      this.pendingRows = [];
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.error.message);
      this.pendingRows = [];
    }
  }

  exportExcel() {
    const {data, columns} = this.formatExcelModel();
    this.exportCsvService.exportAsCustomExcelFile(data, 'Budget', columns);
  }

  formatExcelModel() {
    const data = [];
    for (const record of this.dataSource.data) {
      const model = {
        regional_name: record.regional_name,
        inspector_name: record.inspector_name,
        sales_group_name: record.sales_group_name,
        material_id: record.material_id,
        category_name: record.category_name,
        is_led_name: record.is_led_name,
        initial_quantity: record.initial_quantity,
        returns_to_repair: record.returns_to_repair,
        returns_ready: record.returns_ready,
        redistributions: record.redistributions,
        disasters: record.disasters,
        bound: record.bound,
        sent: record.sent,
        physical_budget: record.physical_budget,
        available_budget: record.available_budget
      };
      data.push(model);
    }

    const columns = [{
      key: 'regional_name',
      label: 'Περιφέρεια',
    }, {
      key: 'inspector_name',
      label: 'Επιθεωρητής',
    }, {
      key: 'sales_group_name',
      label: 'Δρομολόγιο',
    }, {
      key: 'material_id',
      label: 'Κωδ. Είδους',
    },
      {
        key: 'category_name',
        label: 'Κατηγορία',
      }, {
        key: 'is_led_name',
        label: 'LED',
      }, {
        key: 'initial_quantity',
        label: 'Αρχικό budget',
      }, {
        key: 'returns_to_repair',
        label: 'Επιστροφές προς επισκευή',
      },
      {
        key: 'returns_ready',
        label: ' Επιστροφές έτοιμα',
      }, {
        key: 'redistributions',
        label: 'Ανακατανομές Budget',
      }, {
        key: 'disasters',
        label: 'Καταστροφές',
      }, {
        key: 'bound',
        label: 'Δεσμευμένα',
      },
      {
        key: 'sent',
        label: 'Αποστολές',
      }, {
        key: 'physical_budget',
        label: 'Φυσικό απόθεμα',
      }, {
        key: 'available_budget',
        label: 'Διαθέσιμο απόθεμα',
      }
    ];

    return {data, columns};
  }

  exportPDF() {
    this.matDialog.open(PdfCreatorComponent, {
      data: {
        source: this.dataSource.data,
        model: 'usedBudget',
        panelClass: 'no-padding-dialog',
        filters: {
          regionals: {original: this.regionals, selected: this.selectedRegionals},
          materials: {original: this.materials, selected: this.selectedMaterials},
          salesGroup: {original: this.salesGroups, selected: this.selectedSalesGroup},
          inspectors: {original: this.inspectors, selected: this.selectedInspectors},
          categories: {original: this.categories, selected: this.selectedCategories},
        }
      }
    });
  }

  clearRow(row, keepNewValue?) {
    const index = this.dataSource.data.findIndex(t => t.id === row.id);
    const initialIndex = this.initialData.findIndex(t => t.id === row.id);

    const initialRow = this.initialData[initialIndex];
    this.dataSource.data[index].redistributions = +initialRow.redistributions;
    this.dataSource.data[index].available_budget = +initialRow.available_budget;
    this.dataSource.data[index].physical_budget = +initialRow.physical_budget;
    if (!keepNewValue) {
      this.dataSource.data[index]['new-redistribution'] = '';
    }
    const tempIndex = this.pendingRows.findIndex(r => r.rowId === row.id);
    if (tempIndex !== -1) {
      this.pendingRows.splice(tempIndex, 1);
    }
  }

  async refresh() {
    try {
      this.loadingService.show();
      await this.initDataSource(true);
      this.loadingService.hide();
      this.pendingRows = [];
    } catch (e) {
      this.loadingService.hide();
      this.toastMessagesService.toastErrorMessage(e.error.message);
    }
  }
}
